import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, action, testid }) {
  return (
    <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-testid={testid}>
      <div className="min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function ListRow({ title, subtitle, left, right, badge, onClick, testid, chevron = true }) {
  return (
    <div className="cc-list-row" onClick={onClick} data-testid={testid} role="button" tabIndex={0}>
      <div className="flex items-center gap-3 min-w-0">
        {left}
        <div className="min-w-0">
          <div className="font-semibold text-slate-800 truncate">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {badge}
        {right}
        {chevron && <ChevronRight size={18} className="text-slate-300" />}
      </div>
    </div>
  );
}

export function StatCard({ label, value, sub, tone = "default", icon: Icon, testid, onClick }) {
  const tones = {
    default: "text-slate-900",
    success: "text-brand",
    warning: "text-amber-500",
    error: "text-red-500",
  };
  const interactive = typeof onClick === "function";
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
  };
  return (
    <div
      className={cn(
        "cc-card p-5",
        interactive && "cursor-pointer transition-all hover:border-brand hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-link",
      )}
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label}: ${value}` : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
        {Icon && <Icon size={18} className="text-slate-300" />}
      </div>
      <div className={cn("font-display text-3xl font-extrabold mt-2 truncate", tones[tone])}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 break-words">{sub}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    completed: "bg-brand-light text-brand-dark",
    passed: "bg-brand-light text-brand-dark",
    pending: "bg-slate-100 text-slate-600",
    in_progress: "bg-blue-50 text-blue-600",
    overdue: "bg-red-50 text-red-600",
    failed: "bg-red-50 text-red-600",
    exception: "bg-red-50 text-red-600",
    sent: "bg-brand-light text-brand-dark",
    draft: "bg-slate-100 text-slate-500",
    scheduled: "bg-amber-50 text-amber-600",
    cancelled: "bg-slate-100 text-slate-400 line-through",
  };
  const label = (status || "").replace("_", " ");
  return <span className={cn("text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize", map[status] || "bg-slate-100 text-slate-600")}>{label}</span>;
}

export function EmptyState({ icon: Icon, title, message, action, testid }) {
  return (
    <div className="cc-card p-10 flex flex-col items-center text-center" data-testid={testid}>
      {Icon && <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4"><Icon className="text-slate-400" size={26} /></div>}
      <h3 className="font-display font-bold text-slate-800 text-lg">{title}</h3>
      {message && <p className="text-sm text-slate-500 mt-1 max-w-xs">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000; // minutes
  const abs = Math.abs(diff);
  const fmt = (n, u) => `${Math.round(n)} ${u}${Math.round(n) === 1 ? "" : "s"}`;
  let txt;
  if (abs < 1) txt = "just now";
  else if (abs < 60) txt = fmt(abs, "min");
  else if (abs < 1440) txt = fmt(abs / 60, "hour");
  else txt = fmt(abs / 1440, "day");
  if (txt === "just now") return txt;
  return diff >= 0 ? `${txt} ago` : `in ${txt}`;
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
